import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import SEO from "@/components/common/SEO";
import PageHeader from "@/components/shared/PageHeader";
import { ContactCTA } from "@/components/shared/blocks";
import { blog, blogCategories } from "@/data/site";
import { cn } from "@/lib/utils";

export default function Blog() {
  const [filter, setFilter] = useState("All");
  const list = filter === "All" ? blog : blog.filter((b) => b.category === filter);
  const featured = blog[0];

  return (
    <>
      <SEO title="Blog" description="Insights on AI, cloud, cyber security, DevOps, data and software development from the NeuroTriq TechLabs team." path="/blog" />
      <PageHeader
        overline="Insights"
        title="Ideas, engineering & perspective"
        subtitle="Practical writing on the technology decisions that actually move businesses forward."
      />

      {/* Featured */}
      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Link to={`/blog/${featured.slug}`} className="group grid gap-8 overflow-hidden rounded-3xl border border-border bg-card lg:grid-cols-2" data-testid="blog-featured">
            <div className="relative h-64 overflow-hidden lg:h-auto">
              <img src={featured.image} alt={featured.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <span className="absolute left-5 top-5 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">Featured · {featured.category}</span>
            </div>
            <div className="flex flex-col justify-center p-8 sm:p-10">
              <p className="text-xs text-muted-foreground">{featured.date} · {featured.readTime}</p>
              <h2 className="mt-3 font-display text-3xl font-medium leading-tight tracking-tight transition-colors group-hover:text-primary">{featured.title}</h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">{featured.excerpt}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary">Read article <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" /></span>
            </div>
          </Link>
        </div>
      </section>

      {/* Grid */}
      <section className="pb-24 sm:pb-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex flex-wrap gap-2" data-testid="blog-filters">
            {blogCategories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`blog-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={cn(
                  "rounded-full border px-5 py-2 text-sm font-medium transition-colors",
                  filter === c ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
                )}
              >
                {c}
              </button>
            ))}
          </div>

          <motion.div layout className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {list.map((b) => (
                <motion.div
                  key={b.slug}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.4 }}
                >
                  <Link to={`/blog/${b.slug}`} className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`blog-item-${b.slug}`}>
                    <div className="relative h-48 overflow-hidden">
                      <img src={b.image} alt={b.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <span className="absolute left-4 top-4 rounded-full bg-card/90 px-3 py-1 text-xs font-medium backdrop-blur">{b.category}</span>
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <p className="text-xs text-muted-foreground">{b.date} · {b.readTime}</p>
                      <h3 className="mt-2 font-display text-lg font-medium leading-snug transition-colors group-hover:text-primary">{b.title}</h3>
                      <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{b.excerpt}</p>
                      <span className="mt-4 text-sm font-medium text-primary">Read more →</span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <ContactCTA />
    </>
  );
}
